import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'sonner'
import { AppThunk } from '../../store'
import { handleAPIError } from '../../store/request'
import { clearHardware, disableRSSI, enableRSSI, hardwareService } from './api'
import { createEmptyFormattedHardware, formatHardwareData } from './functions'
import {
  HardwareServiceRequestBody,
  HardwareState,
  HardwareStatusTypeKeys
} from './types'

export const initialHardwareState: HardwareState = {
  formattedHardware: createEmptyFormattedHardware(),
  diagnostics: {
    hardwareIndex: [],
    countMatrix: [],
    rssiMatrix: [],
    imuLenMatrix: [],
    distanceMatrix: [],
    status: {
      online: {
        tags: [],
        balls: [],
        anchors: []
      },
      offline: {
        tags: [],
        balls: [],
        anchors: []
      },
      sleep: {
        tags: [],
        balls: [],
        anchors: []
      }
    },
    finalAnchorIndex: 0,
    masterSignalStrength: [],

    hardwareState: {},
    availableSeats: 0
  },

  selectedDevice: null,
  collapsedHardwareIds: {},

  serviceActionPendingDeviceList: []
}

export const hardwareSlice = createSlice({
  name: 'hardware',
  initialState: initialHardwareState,
  reducers: {
    setDiagnositcs: (
      state,
      action: PayloadAction<HardwareState['diagnostics']>
    ) => {
      state.diagnostics = action.payload
    },
    setFormattedHardware: (
      state,
      action: PayloadAction<HardwareState['formattedHardware']>
    ) => {
      state.formattedHardware = action.payload
    },
    setSelectedDevice: (
      state,
      action: PayloadAction<HardwareState['selectedDevice']>
    ) => {
      state.selectedDevice = action.payload
    },
    addServiceActionPendingForDevices: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.serviceActionPendingDeviceList = [
        ...state.serviceActionPendingDeviceList,
        ...action.payload
      ]
    },
    removeServiceActionPendingForDevices: (
      state,
      action: PayloadAction<string[]>
    ) => {
      const newList = [...state.serviceActionPendingDeviceList]

      // Go through each id in action.payload and remove it from the list //
      action.payload.forEach((id) => {
        const index = state.serviceActionPendingDeviceList.indexOf(id)
        if (index > -1) {
          newList.splice(index, 1)
        }
      })

      return {
        ...state,
        serviceActionPendingDeviceList: newList
      }
    },
    toggleCollapsedHardware: (state, action: PayloadAction<string>) => {
      const encodedId = action.payload
      const isCurrentlyCollapsed = state.collapsedHardwareIds[encodedId]

      return {
        ...state,
        collapsedHardwareIds: {
          ...state.collapsedHardwareIds,
          [encodedId]: !isCurrentlyCollapsed
        }
      }
    },
    toggleCollapseAllHardware: (state, action: PayloadAction<boolean>) => {
      const newCollapsedHardwareIds: {
        [key: string]: boolean
      } = {}
      // collapse all
      if (action.payload) {
        state.diagnostics.hardwareIndex.forEach((hardwareId) => {
          newCollapsedHardwareIds[hardwareId] = false
        })
      } else {
        // expand all
        state.diagnostics.hardwareIndex.forEach((hardwareId) => {
          newCollapsedHardwareIds[hardwareId] = true
        })
      }

      return {
        ...state,
        collapsedHardwareIds: newCollapsedHardwareIds
      }
    }
  }
})

export const reducer = hardwareSlice.reducer

export const {
  setDiagnositcs,
  setFormattedHardware,
  setSelectedDevice,
  addServiceActionPendingForDevices,
  removeServiceActionPendingForDevices,
  toggleCollapsedHardware,
  toggleCollapseAllHardware
} = hardwareSlice.actions

// ====== //

export function formatDiagnosticsWebsocketAndUpdateStore(
  diagnostics: HardwareState['diagnostics']
): AppThunk {
  return (dispatch, getState) => {
    // Convert null status (online, sleep, offline) values to empty arrays
    // Move this to formatHardwareData function
    for (const type in diagnostics.status.online) {
      const typeKey = type as HardwareStatusTypeKeys
      if (!diagnostics.status.online[typeKey])
        diagnostics.status.online[typeKey] = []
    }
    for (const type in diagnostics.status.offline) {
      const typeKey = type as HardwareStatusTypeKeys
      if (!diagnostics.status.offline[typeKey])
        diagnostics.status.offline[typeKey] = []
    }
    for (const type in diagnostics.status.sleep) {
      const typeKey = type as HardwareStatusTypeKeys
      if (!diagnostics.status.sleep[typeKey])
        diagnostics.status.sleep[typeKey] = []
    }

    if (!diagnostics.hardwareState)
      diagnostics.hardwareState = initialHardwareState.diagnostics.hardwareState

    if (!diagnostics.hardwareIndex) diagnostics.hardwareIndex = []

    dispatch(setDiagnositcs(diagnostics))

    const pitches = getState().pitches
    const formattedHardware = formatHardwareData(
      diagnostics,
      pitches?.inUse?.anchors ?? {}
    )
    dispatch(setFormattedHardware(formattedHardware))
  }
}

export function handleServiceAction(
  tagIds: HardwareServiceRequestBody['tagIds'],
  type: HardwareServiceRequestBody['type'],
  hideSuccessModal?: boolean
): AppThunk {
  return async (dispatch) => {
    // Add list of devices to service action pending list
    // - this is to avoid spamming the MS and for displaying loaders over the buttons //
    addServiceActionPendingForDevices(tagIds)
    try {
      await dispatch(
        hardwareService.initiate({
          tagIds,
          type
        })
      ).unwrap()
      removeServiceActionPendingForDevices(tagIds)
      if (!hideSuccessModal) {
        toast.success(`${type} success`)
      }
    } catch (error) {
      removeServiceActionPendingForDevices(tagIds)
    }
  }
}

export function enableRSSIAction(): AppThunk {
  return async (dispatch) => {
    try {
      await dispatch(enableRSSI.initiate()).unwrap()
      toast.success(`RSSI enabled`)
    } catch (error) {
      dispatch(handleAPIError(error, 'errorDetail'))
    }
  }
}

export function disableRSSIAction(): AppThunk {
  return async (dispatch) => {
    try {
      await dispatch(disableRSSI.initiate()).unwrap()
      toast.success(`RSSI disabled`)
    } catch (error) {
      dispatch(handleAPIError(error, 'errorDetail'))
    }
  }
}

// Clear devices on MS //
export function handleClearHardware(): AppThunk {
  return async (dispatch) => {
    try {
      await dispatch(clearHardware.initiate()).unwrap()
      toast.success(`Diagnostics Cleared`)
    } catch (error) {
      dispatch(handleAPIError(error, 'errorDetail'))
    }
  }
}
